import { OrganizationCodes } from '@revenuemarket/bank';
import qs from 'qs';
import { API } from '~apis/axios/instance';
import { BalanceList } from '~apis/dashboard';
import { ResponseBaseWithData } from '~types/response';

export interface ExchangeRateInfo {
  currency: string;
  date: string;
  rate: number;
}

export interface HomeDashboardResponse {
  totalBalance: ITotalBalance;
  corporateCardApprovalSummary: ICorporateCardApprovalSummary;
  taxInvoiceSummary: ITaxInvoiceSummary;
  cashReceiptSummary: ICashReceiptSummary;
  cashflow: ICashflow;
}
interface ITotalBalance {
  balances: BalanceList;
  exchangeRates?: ExchangeRateInfo[];
}

export enum CardScrapingStatus {
  INIT = 'INIT',
  ENQUEUED = 'ENQUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  FATAL = 'FATAL',
}

interface ICashReceiptSummary {
  sales: ICashReceiptSummaryItem;
  purchases: ICashReceiptSummaryItem;
}
interface ICashReceiptSummaryItem {
  totalAmountSum: number;
  supplyAmountSum: number;
  vatAmountSum: number;
  totalTipAmount: number;
  totalCount: number;
}
interface ICorporateCardApprovalSummary {
  usedAmountByOrganizations: IUsedAmountByOrganizationsItem[];
  totalUsedAmount: number;
}
interface IUsedAmountByOrganizationsItem {
  delta: number;
  organizationCode: OrganizationCodes;
  usedAmountByCard: IUsedAmountByCardItem[];
  totalUsedAmount: number;
  scrapingStatus: CardScrapingStatus;
}
export interface IUsedAmountByCardItem {
  cardNo: string;
  usedAmount: number;
  userNames: string[];
  aliasNames: string[];
}
interface ITaxInvoiceSummary {
  sales: ISales;
  purchases: IPurchases;
}
interface ISales {
  type: string;
  totalSupplyAmount: number;
  totalVatAmount: number;
  totalAmount: number;
  totalCount: number;
}
interface IPurchases {
  type: string;
  totalSupplyAmount: number;
  totalVatAmount: number;
  totalAmount: number;
  totalCount: number;
}

interface ICashflowAccount {
  inflowAmount: number;
  inflowCount: number;
  outflowAmount: number;
  outflowCount: number;
}

interface ICashflow {
  inflowTransactionAmount: number;
  outflowTransactionAmount: number;
  checkingAccount: {
    normal: ICashflowAccount;
    overdraft: ICashflowAccount;
  };
  savingsAccount: ICashflowAccount;
  fundAccount: ICashflowAccount;
  fxAccount: ICashflowAccount;
}

export const getHomeDashboard = ({
  companyId,
  startDate,
  endDate,
}: {
  companyId: string;
  startDate?: string;
  endDate?: string;
}) => {
  const queryString = qs.stringify({
    startDate,
    endDate,
  });
  return API.get<ResponseBaseWithData<HomeDashboardResponse>>(
    `/api/v3/companies/${companyId}/home-dashboard${queryString ? `?${queryString}` : ''}`,
  );
};

// ==================== Checking Account Seen Types ====================

export type SeenType = 'INDIVIDUAL' | 'BULK';

export interface MarkAsSeenRequest {
  bankAccountIds: number[];
  seenType: SeenType;
  seenAt: string;
}

export interface AccountSeenStatus {
  bankAccountId: number;
  hasUnseen: boolean;
}

export interface SeenStatusResponse {
  hasUnseen: boolean;
  accounts: AccountSeenStatus[];
}

// ==================== Checking Account Seen APIs ====================

export const markCheckingAccountsAsSeen = ({
  companyId,
  request,
}: {
  companyId: string;
  request: MarkAsSeenRequest;
}) => {
  return API.post<ResponseBaseWithData<void>>(
    `/api/v3/companies/${companyId}/home-dashboard/checking-accounts/last-seen`,
    { data: request },
  );
};

export const getCheckingAccountsSeenStatus = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<SeenStatusResponse>>(
    `/api/v3/companies/${companyId}/home-dashboard/checking-accounts/seen-status`,
  );
};
